import { useEffect, useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { 
    FileCode2,
    Search,
    AlertCircle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { getTemplates } from '@/api/templates';
import type { Template } from '@/api/templates';
import { containerVariants, itemVariants } from '@/components/animations';
import { formatPieceDisplayName, getUniquePieces } from '@/api/templates/templates.utils';

// Piece chip - simple text display
const PieceChip = ({ pieceName }: { pieceName: string }) => {
    const displayName = formatPieceDisplayName(pieceName);
    return (
        <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
            {displayName}
        </span>
    );
};

const TemplateCardSkeleton = () => (
    <Card className="shadow-sm h-full">
        <div className="p-5 space-y-3 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                </div>
                {/* Tags placeholder */}
                <div className="flex gap-1 flex-shrink-0">
                    <Skeleton className="h-4 w-12 rounded" />
                    <Skeleton className="h-4 w-14 rounded" />
                </div>
            </div>

            {/* Pieces */}
            <div className="flex flex-wrap gap-1 mt-auto">
                <Skeleton className="h-5 w-16 rounded" />
                <Skeleton className="h-5 w-14 rounded" />
                <Skeleton className="h-5 w-18 rounded" />
                <Skeleton className="h-5 w-12 rounded" />
            </div>
        </div>
    </Card>
);

interface TemplatesListProps {
    onTemplateSelect: (templateId: string) => void;
    renderHeader?: (
        hasTemplates: boolean, 
        searchBar?: React.ReactNode
    ) => React.ReactNode;
}

export function TemplatesList({ onTemplateSelect, renderHeader }: TemplatesListProps) {
    const enhancedToast = useEnhancedToast();
    
    const [templates, setTemplates] = useState<Template[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getTemplates({ type: 'CUSTOM' });
            if (response.error) {
                enhancedToast.error({
                    icon: AlertCircle,
                    title: 'Failed to load templates',
                    description: response.error
                });
            } else if (response.data) {
                setTemplates(response.data.data || []);
            }
        } catch {
            enhancedToast.error({
                icon: AlertCircle,
                title: 'Error loading templates',
                description: 'Failed to fetch workflow templates'
            });
        } finally {
            setIsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    // Filter templates based on search query
    const filteredTemplates = useMemo(() => {
        if (!searchQuery.trim()) return templates;
        
        const query = searchQuery.toLowerCase();
        return templates.filter(template => 
            template.name.toLowerCase().includes(query) ||
            template.summary.toLowerCase().includes(query) ||
            template.description.toLowerCase().includes(query) ||
            template.tags.some(tag => tag.title.toLowerCase().includes(query)) ||
            template.pieces.some(piece => piece.toLowerCase().includes(query))
        );
    }, [templates, searchQuery]);

    if (isLoading) {
        return (
            <>
                {renderHeader && renderHeader(false)}
                <div className="flex-1 overflow-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {[...Array(6)].map((_, i) => (
                                <TemplateCardSkeleton key={i} />
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </>
        );
    }

    // Search bar component to pass to header
    const searchBar = templates.length > 0 ? (
        <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-[200px] lg:w-[250px] pl-9"
            />
        </div>
    ) : null;

    return (
        <>
            {renderHeader && renderHeader(templates.length > 0, searchBar)}
            <div className="flex-1 overflow-auto">
                {templates.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            className="w-full -mt-12"
                        >
                            <EmptyState
                                icon={FileCode2}
                                title="No Templates Available"
                                description="There are no workflow templates available at the moment. Templates help you quickly create pre-configured workflows."
                            />
                        </motion.div>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        {filteredTemplates.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className="flex items-center justify-center -mt-12"
                                style={{ height: 'calc(100vh - 200px)' }}
                            >
                                <div className="text-center">
                                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                        <Search className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">No Matching Templates</h3>
                                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                        No templates found matching "{searchQuery}". Try adjusting your search.
                                    </p>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        Clear Search
                                    </Button>
                                </div>
                            </motion.div>
                        ) : (
                        <motion.div
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-fr"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {filteredTemplates.map((template) => {
                                const uniquePieces = getUniquePieces(template.pieces);
                                const hasTags = template.tags.length > 0;

                                return (
                                <motion.div key={template.id} variants={itemVariants} className="h-full">
                                    <Card
                                        className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer h-full flex flex-col"
                                        onClick={() => onTemplateSelect(template.id)}
                                    >
                                        {/* Subtle gradient on hover */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                                        {/* Main Content */}
                                        <div className="relative p-5 space-y-3 flex-1 flex flex-col">
                                            {/* Header with Icon, Title, and Tags */}
                                            <div className="flex items-start gap-3">
                                                {/* Icon */}
                                                <div className="relative flex-shrink-0">
                                                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 flex items-center justify-center ring-1 ring-primary/10 shadow-sm">
                                                        <FileCode2 className="h-5 w-5 text-primary" />
                                                    </div>
                                                </div>

                                                {/* Title and Summary */}
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="text-base font-semibold tracking-tight truncate">
                                                        {template.name}
                                                    </h3>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                        {template.summary || template.description}
                                                    </p>
                                                </div>

                                                {/* Tags - top right */}
                                                {hasTags && (
                                                    <div className="flex flex-wrap gap-1 justify-end flex-shrink-0 max-w-[120px]">
                                                        {template.tags.slice(0, 2).map((tag, index) => (
                                                            <Badge 
                                                                key={index} 
                                                                variant="secondary"
                                                                className="text-[10px] px-1.5 py-0"
                                                                style={{ 
                                                                    backgroundColor: tag.color ? `${tag.color}20` : undefined,
                                                                    borderColor: tag.color || undefined
                                                                }}
                                                            >
                                                                {tag.title}
                                                            </Badge>
                                                        ))}
                                                        {template.tags.length > 2 && (
                                                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                                                +{template.tags.length - 2}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* All Pieces */}
                                            {uniquePieces.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-auto">
                                                    {uniquePieces.map((piece, index) => (
                                                        <PieceChip key={index} pieceName={piece} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </motion.div>
                                );
                            })}
                        </motion.div>
                        )}
                    </motion.div>
                )}
            </div>
        </>
    );
}
